import React, { useState, useEffect } from "react";
import {
  Typography,
} from "@material-ui/core";
import { useStore } from "@store/store";
import {
  VIEW_BACK_DATED_EVENT_LOG,
  VIEW_BACK_DATED_EVENT_LOG_SUCCESS,
  VIEW_BACK_DATED_EVENT_LOG_FAILURE
} from "@utils/actionTypes";
import {
  utcToTimezone,
  utcToStartDateTimezone,
  utcToEndDateTimezone,
  formatAustralianCurrency
} from "@utils/commonFunctions";
import Header from "@components/header";
import { materialCommonStyles } from "@utils/materialCommonStyles";
import API from "@services/axios";
import { ViewLogStyle } from "./style";
import {
  Link,
  useHistory,
  useParams
} from "react-router-dom";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import {
  routes,
  timezone
} from "@utils/constant";
import Loader from "@components/loader";

function BackDatedEventViewLog() {
  const classes = ViewLogStyle();
  const { id } = useParams();
  const [state, dispatch] = useStore();
  const history = useHistory();
  const [data, setData] = useState({});
  const [existingDraftInvoiceRejected, setExistingDraftInvoiceRejected] = useState(false);
  const [existingNewInvoiceGenerated, setExistingNewInvoiceGenerated] = useState(false);
  const [expiredFuelSurcharge, setExpiredFuelSurcharge] = useState(null);

  const getBackDatedEventLog = () => {
    dispatch({ type: VIEW_BACK_DATED_EVENT_LOG });
    API.get(`customers/backDatedEventLog/${id}`)
      .then((response) => {
        dispatch({
          type: VIEW_BACK_DATED_EVENT_LOG_SUCCESS,
          payload: response.data.data,
        });

        setData(response?.data?.data);
        setExistingDraftInvoiceRejected(response?.data?.data?.impactedInvoices.some(item => item.impactedSourceInvoices !== null));
        setExistingNewInvoiceGenerated(response?.data?.data?.impactedInvoices.some(item => item.impactedTargetInvoices !== null));

        const existingFuelSurchargesArr = response?.data?.data?.impactedJobs.map(job => job.fuelSurcharges);
        if (existingFuelSurchargesArr && existingFuelSurchargesArr.length > 0) {
          const latestEndDate = existingFuelSurchargesArr.reduce((latest, item) => {
            return new Date(item.endDate) > new Date(latest) ? item.endDate : latest;
          }, existingFuelSurchargesArr[0].endDate);

          const latestEndDateItems = existingFuelSurchargesArr.filter(item => item.endDate === latestEndDate);

          const result = latestEndDateItems.sort(
            (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
          )[0];

          setExpiredFuelSurcharge(result);
        }
      })
      .catch((error) => {
        dispatch({ type: VIEW_BACK_DATED_EVENT_LOG_FAILURE, payload: error });
        if (error?.response?.status === 404) {
          history.push(routes.pageNotFound);
        }
      });
  };

  const formatDateToTimezone = (date) => {
    return date ? utcToTimezone(date, timezone.SYDNEY, "DD-MM-YYYY HH:mm") + " NSW" : "N/A";
  }

  useEffect(() => {
    getBackDatedEventLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <>
      <Header />
      <Loader loading={ state?.customer?.loadingBackDatedEventLog }/>
      <div className={classes.ViewLogWrapper}>
        <div className="viewlog-page wrapper">
          <div className="inner-page">
            <div className={classes.backLinkWrapper}>
              <Link to="#" onClick={() => history.goBack()} className={classes.backToPage}>
                <ArrowBackIosIcon/> Back to Impacted Customers
              </Link>
            </div>
            <div className="title-panel">
              <Typography variant="h1">Change Log</Typography>
            </div>

            <div className="summary-holder">
              <div className="summary">
                <div className="title">Change Summary</div>
                <div className="content">
                  <div className="section">
                    <header>Customer / City:</header>
                    <b>{data?.customers?.name ? data?.customers?.name : "N/A"} - {data?.cities?.name ? data?.cities?.name : "N/A"}</b>
                  </div>
                  <div className="section">
                    <header>Action Performed By:</header>
                    <b>{data?.users?.name ? data?.users?.name : "N/A"}</b>
                  </div>
                  <div className="section">
                    <header>Date Applied:</header>
                    <b>
                      {
                        formatDateToTimezone(data?.updatedAt)
                      }
                    </b>
                  </div>
                </div>
              </div>
            </div>

            <div className="detail-holder">
              <div className="detail">
                <div className="title">Execution Log</div>
                <div className="content">
                  <table width="100%" cellPadding="0" cellSpacing="0">
                    {/* Type = "Rate Updated"         -> updated-color
                        Type = "Rate Expired"         -> expired-color
                        Type = "Invoices Rejected"    -> rejected-color
                        Type = "Invoices Generated"   -> generated-color
                        Type = "Charges Recalculated" -> recalculated-color
                    */}
                    <tbody>
                    {data?.fuelSurcharges && (
                      <>
                        <tr> {/* Type = "Rate Updated"*/}
                          <td className="first">
                            <div className="circle updated-color"></div>
                            <div className="eraser"></div>
                            <div className="line"></div>
                          </td>
                          <td className="second updated">
                            <div className="header">
                              <div className="mobile">
                                {
                                  formatDateToTimezone(data?.fuelSurcharges?.updatedAt)
                                }
                              </div>
                              Fuel Surcharge Rate Updated
                            </div>
                            <div className="info">
                              A new rate of <span>{data?.fuelSurcharges?.fuelSurcharge}%</span> was applied, effective from <b>{utcToStartDateTimezone(data?.fuelSurcharges?.startDate, data?.cities?.timezone, "DD-MM-YYYY HH:mm z")}</b>.
                            </div>
                          </td>
                          <td className="third">
                            {
                              formatDateToTimezone(data?.fuelSurcharges?.updatedAt)
                            }
                          </td>
                        </tr>
                        <tr> {/* Type = "Rate Updated"*/}
                          <td colSpan="3" className="space"></td>
                        </tr>
                      </>
                    )}

                    {expiredFuelSurcharge && (
                      <>
                        <tr> {/* Type = "Rate Expired"*/}
                          <td className="first">
                            <div className="circle expired-color"></div>
                            <div className="eraser"></div>
                            <div className="line"></div>
                          </td>
                          <td className="second expired">
                            <div className="header">
                              <div className="mobile">
                                {
                                  formatDateToTimezone(expiredFuelSurcharge?.updatedAt)
                                }
                              </div>
                              Previous Rate Expired
                            </div>
                            <div className="info">
                              The active rate of <span>{expiredFuelSurcharge?.fuelSurcharge}%</span> was automatically end-dated
                              on <b>{utcToEndDateTimezone(expiredFuelSurcharge?.endDate, data?.cities?.timezone, "DD-MM-YYYY HH:mm z")}</b> and its status set to "Expired".
                            </div>
                          </td>
                          <td className="third">
                            {
                              formatDateToTimezone(expiredFuelSurcharge?.updatedAt)
                            }
                          </td>
                        </tr>
                        <tr> {/* Type = "Rate Expired"*/}
                          <td colSpan="3" className="space"></td>
                        </tr>
                      </>
                    )}

                    {data?.impactedInvoices && data?.impactedInvoices?.length > 0 && existingDraftInvoiceRejected  &&  (
                      <>
                        <tr> {/* Type = "Invoices Rejected"*/}
                          <td className="first">
                            <div className="circle rejected-color"></div>
                            <div className="eraser"></div>
                            <div className="line"></div>
                          </td>
                          <td className="second">
                            <div className="header">
                              <div className="mobile">
                                {
                                  formatDateToTimezone(data?.updatedAt)
                                }
                              </div>
                              Draft Invoices Rejected
                            </div>
                            <div className="info">
                              The following draft invoices were rejected to allow for re-generation with the new rate:
                            </div>
                          </td>
                          <td className="third">
                            {
                              formatDateToTimezone(data?.updatedAt)
                            }
                          </td>
                        </tr>
                        <tr> {/* Type = "Invoices Rejected"*/}
                          <td>&nbsp;</td>
                          <td className="list">
                            <ul>
                              {data?.impactedInvoices
                                ?.filter(item => item?.impactedSourceInvoices)
                                ?.sort((a, b) => {
                                  const aId = a.impactedSourceInvoices?.id;
                                  const bId = b.impactedSourceInvoices?.id;
                                  return aId - bId;
                                })
                                ?.map((item) =>
                                  (
                                    <li key={item?.impactedSourceInvoices?.id}>
                                      Invoice <b>#{item?.impactedSourceInvoices?.id}</b>
                                      <ul className="child-invoice">
                                        {(item?.impactedSourceInvoices?.childInvoice && item?.impactedSourceInvoices?.childInvoice?.length > 0) ? item?.impactedSourceInvoices?.childInvoice?.map((childItem) =>
                                          (
                                            <li key={childItem?.id}>
                                              Invoice
                                              <b>&nbsp;<Link
                                                to={`${routes.invoiceDetail}/${childItem?.id}`}
                                                target={"_blank"}>#{childItem?.id}</Link>
                                              </b>&nbsp;
                                              <span className="break">(Original Amount: <b className={childItem?.net ? childItem?.net < 0 ? "negative" : "" : ""}>{childItem?.net ? formatAustralianCurrency(childItem?.net) : "$0.00"}</b>)</span>
                                            </li>
                                          ))
                                          :
                                          ""
                                        }
                                      </ul>
                                    </li>
                                  )
                                )
                              }
                            </ul>
                          </td>
                          <td>&nbsp;</td>
                        </tr>
                        <tr> {/* Type = "Invoices Rejected"*/}
                          <td colSpan="3" className="space"></td>
                        </tr>
                      </>
                    )}

                    {data?.impactedInvoices && data?.impactedInvoices?.length > 0 && existingNewInvoiceGenerated  &&  (
                      <>
                        <tr> {/* Type = "Invoices Generated"*/}
                          <td className="first">
                            <div className="circle generated-color"></div>
                            <div className="eraser"></div>
                            <div className="line"></div>
                          </td>
                          <td className="second">
                            <div className="header">
                              <div className="mobile">
                                {
                                  formatDateToTimezone(data?.updatedAt)
                                }
                              </div>
                              New Invoices Generated
                            </div>
                            <div className="info">
                              New draft invoices were created with updated charges:
                            </div>
                          </td>
                          <td className="third">
                            {
                              formatDateToTimezone(data?.updatedAt)
                            }
                          </td>
                        </tr>
                        <tr> {/* Type = "Invoices Generated"*/}
                          <td>&nbsp;</td>
                          <td className="list">
                            <ul>
                              {data?.impactedInvoices
                                ?.filter(item => item?.impactedTargetInvoices)
                                ?.sort((a, b) => {
                                  const aId = a.impactedTargetInvoices?.id;
                                  const bId = b.impactedTargetInvoices?.id;
                                  return aId - bId;
                                })
                                ?.map((item) =>
                                  (
                                    <li key={item?.impactedTargetInvoices?.id}>
                                      Invoice <b>#{item?.impactedTargetInvoices?.id}</b> (earlier #{item?.invoiceId})
                                      <ul className="child-invoice">
                                        {(item?.impactedTargetInvoices?.childInvoice && item?.impactedTargetInvoices?.childInvoice?.length > 0) ? item?.impactedTargetInvoices?.childInvoice?.map((childItem) =>
                                          (
                                            <li key={childItem?.id}>
                                              Invoice
                                              <b>&nbsp;<Link
                                                to={`${routes.invoiceDetail}/${childItem?.id}`}
                                                target={"_blank"}>#{childItem?.id}</Link>
                                              </b>&nbsp;
                                              <span className="break">(Amount: <b className={childItem?.net ? childItem?.net < 0 ? "negative" : "" : ""}>{childItem?.net ? formatAustralianCurrency(childItem?.net) : "$0.00"}</b>)</span>
                                            </li>
                                          ))
                                          :
                                          ""
                                        }
                                      </ul>
                                    </li>
                                  )
                                )
                              }
                            </ul>
                          </td>
                          <td>&nbsp;</td>
                        </tr>
                        <tr> {/* Type = "Invoices Generated"*/}
                          <td colSpan="3" className="space"></td>
                        </tr>
                      </>
                    )}

                    {data?.impactedJobs && data?.impactedJobs?.length > 0 && (
                      <>
                        <tr> {/* Type = "Charges Recalculated"*/}
                          <td className="first">
                            <div className="circle recalculated-color"></div>
                            <div className="eraser"></div>
                            <div className="line"></div>
                          </td>
                          <td className="second">
                            <div className="header">
                              <div className="mobile">
                                {
                                  formatDateToTimezone(data?.updatedAt)
                                }
                              </div>
                              Job Charges Recalculated
                            </div>
                            <div className="info">
                              {data?.impactedJobs?.length} job{data?.impactedJobs?.length > 1 ? "s":""} had {data?.impactedJobs?.length > 1 ? "their":"its"} charges recalculated as part of this change:
                            </div>
                          </td>
                          <td className="third">
                            {
                              formatDateToTimezone(data?.updatedAt)
                            }
                          </td>
                        </tr>
                        <tr> {/* Type = "Charges Recalculated"*/}
                          <td>&nbsp;</td>
                          <td className="list job">
                            <ul>
                              {data?.impactedJobs
                                ?.filter(item => item?.jobs)
                                ?.sort((a, b) => {
                                  const aId = a.jobs?.id;
                                  const bId = b.jobs?.id;
                                  return aId - bId;
                                })
                                ?.map((item) =>
                                  (
                                  <li key={item?.jobs?.id}>
                                    Job
                                    <b>&nbsp;<Link
                                      to={`${routes.jobDetail}/${item?.jobs?.id}`}
                                      target={"_blank"}>{item?.jobs?.id}</Link>
                                    </b>&nbsp;
                                    <span className={item?.originalCharge ? item?.originalCharge < 0 ? "break negative" : "break" : "break"}>
                                      ({item?.originalCharge ? formatAustralianCurrency(item?.originalCharge) : "$0.00"}{' -> '}
                                      <b className={item?.jobs?.net ? item?.jobs?.net < 0 ? "negative" : "" : ""}>
                                        {item?.jobs?.net ? formatAustralianCurrency(item?.jobs?.net) : "$0.00"}
                                      </b>)
                                    </span>
                                  </li>
                                  )
                                )
                              }
                            </ul>
                          </td>
                          <td>&nbsp;</td>
                        </tr>
                      </>
                    )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BackDatedEventViewLog;
